import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatCents } from "@/lib/money";
import { ChevronLeft, ChevronRight, AlertTriangle, Check, Circle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function currentMonthKey() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function shiftMonth(key, delta) {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
function firstWeekdayOffset(key) {
  const [y, m] = key.split("-").map(Number);
  // Monday-first: convert JS getDay (Sun=0..Sat=6) to Mon=0..Sun=6
  return (new Date(y, m - 1, 1).getDay() + 6) % 7;
}

const WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function MoneyBills() {
  const [month, setMonth] = useState(currentMonthKey());
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/money/bills-calendar?month=${month}`);
    setData(r.data);
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const togglePaid = async (entry) => {
    if (entry.source !== "item") { toast("This bill isn't linked to an item yet — pay it via Today or Board."); return; }
    await api.post(`/money/bills/${entry.id}/toggle-paid`);
    load();
  };

  if (!data) return <div className="text-sm text-surface-onVariant">Loading calendar…</div>;

  const offset = firstWeekdayOffset(month);
  const totalCells = Math.ceil((offset + data.last_day) / 7) * 7;
  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const day = i - offset + 1;
    const inMonth = day >= 1 && day <= data.last_day;
    cells.push({ day: inMonth ? day : null, bills: inMonth ? data.entries.filter((e) => e.day === day) : [] });
  }

  const paid = data.entries.filter((e) => e.is_paid).reduce((s, e) => s + (e.amount_cents || 0), 0);
  const unpaid = data.total_cents - paid;

  return (
    <div data-testid="money-bills" className="space-y-6">
      {/* Header */}
      <div className="rounded-hero bg-surface-container p-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <button data-testid="bills-prev" onClick={() => setMonth((m) => shiftMonth(m, -1))} className="p-2 rounded-full hover:bg-primary/10 text-surface-onVariant" aria-label="Previous"><ChevronLeft className="w-4 h-4" /></button>
          <div className="text-lg font-medium text-surface-on min-w-[10rem] text-center" data-testid="bills-month">{monthLabel(data.month)}</div>
          <button data-testid="bills-next" onClick={() => setMonth((m) => shiftMonth(m, +1))} className="p-2 rounded-full hover:bg-primary/10 text-surface-onVariant" aria-label="Next"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="ml-auto text-right">
          <div className="text-xs uppercase tracking-widest text-surface-onVariant">Total this month</div>
          <div className="text-2xl font-medium text-surface-on tabular-nums" data-testid="bills-total">{formatCents(data.total_cents)}</div>
          <div className="text-xs text-surface-onVariant mt-0.5">
            <span className="text-primary">{formatCents(paid)} paid</span> · {formatCents(unpaid)} outstanding
          </div>
        </div>
      </div>

      {/* Crunch alert */}
      {data.crunch && (
        <div className="rounded-hero bg-danger-container p-5 flex items-start gap-4" data-testid="crunch-alert">
          <div className="w-10 h-10 rounded-full bg-danger text-danger-container flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-danger">Cashflow crunch — days {data.crunch.start_day}–{data.crunch.end_day}</div>
            <div className="text-sm text-danger/80 mt-1">
              {data.crunch.bills.map((b) => b.name).join(", ")} land within a few days, totalling{" "}
              <span className="font-medium">{formatCents(data.crunch.amount_cents)}</span>.
            </div>
            <div className="text-sm text-danger font-medium mt-2">
              Set aside {formatCents(data.crunch.recommendation_cents)} by day {data.crunch.recommendation_day}.
            </div>
          </div>
        </div>
      )}

      {/* Calendar grid */}
      <div className="rounded-hero bg-surface-container p-4 md:p-6">
        <div className="grid grid-cols-7 gap-2 mb-2">
          {WEEK_LABELS.map((l) => (
            <div key={l} className="text-[10px] uppercase tracking-widest text-surface-onVariant text-center font-medium py-1">{l}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2" data-testid="bills-grid">
          {cells.map((cell, i) => (
            <div key={i} className={cn(
              "aspect-square min-h-[90px] rounded-2xl p-2 flex flex-col relative",
              cell.day ? "bg-surface border border-outline-variant" : "opacity-30",
              cell.bills.length > 0 && "border-primary/40"
            )}>
              {cell.day && (
                <>
                  <div className="text-xs text-surface-onVariant">{cell.day}</div>
                  <div className="flex-1 mt-1 space-y-1 overflow-hidden">
                    {cell.bills.slice(0, 3).map((b) => (
                      <button
                        key={b.id}
                        data-testid={`bill-${b.id}`}
                        onClick={() => togglePaid(b)}
                        title={b.name}
                        className={cn(
                          "w-full text-left px-1.5 py-1 rounded-md text-[10px] font-medium leading-tight truncate transition-colors duration-200 ease-md",
                          b.is_paid ? "bg-success-container text-success line-through opacity-70" : "bg-primary-container text-primary-onContainer hover:brightness-95"
                        )}
                      >
                        <span className="inline-flex items-center gap-1">
                          {b.is_paid ? <Check className="w-2.5 h-2.5 shrink-0" /> : <Circle className="w-2.5 h-2.5 shrink-0" />}
                          <span className="truncate">{b.name}</span>
                        </span>
                        {b.amount_cents && <div className="text-[9px] mt-0.5 opacity-80">{formatCents(b.amount_cents)}</div>}
                      </button>
                    ))}
                    {cell.bills.length > 3 && <div className="text-[9px] text-surface-onVariant">+{cell.bills.length - 3} more</div>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {data.entries.length === 0 && (
        <div className="rounded-hero bg-surface-container p-10 text-center text-sm text-surface-onVariant">
          No bills scheduled for {monthLabel(data.month)}.
        </div>
      )}
    </div>
  );
}
